import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import ReactMarkdown from 'react-markdown'
import shortid from 'shortid'

import Button from './button'
import {
  getShortLinkCandidatesGQL,
  validateShortLinkCandidateGQL,
} from '../store/actions'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import styles from '../component-styles/editable-short-link-input.module.scss'
import { getItemByKeyValue } from '../helpers'
import { ShortLinkInput } from './input'
import { maxShortLinkAliasLength } from '../api-client/constants'

interface EditableShortLinkInputProps {
  shortLink: string
  setShortLink: (val: string) => void
  setShowBrandedLinkModal?: (val: boolean) => void
  defaultKey?: string
  onChangeStatus?: (val: UrlStatus) => void
  showDropDownOnTop?: boolean
  canChangeShortLink?: boolean
}

export default function EditableShortLinkInput({
  shortLink,
  setShortLink,
  setShowBrandedLinkModal,
  defaultKey = '',
  onChangeStatus,
  showDropDownOnTop = false,
  canChangeShortLink = true,
}: EditableShortLinkInputProps): React.ReactElement {
  // TODO: make this an account level option
  // const canChangeShortLink = true
  const dispatch = useDispatch()

  const [shortLinkKey, setShortLinkKey] = useState(defaultKey)
  const [shortLinkIsCustom, setShortLinkIsCustom] = useState(false)
  const [shortLinkMessage, setShortLinkMessage] = useState<string>('')
  const [shortLinkStatus, setShortLinkStatus] = useState<UrlStatus>('')
  const { shortLinkIdList } = useSelector(
    (s: any): CampaignCodesState => s.campaignCodes,
  )
  const { validateShortLinkCandidate } = useSelector(
    (s: any): GraphQLState => s.graphql,
  )

  useEffect(() => {
    setShortLinkKey(defaultKey)
    setShortLinkIsCustom(false)
  }, [defaultKey])

  useEffect(() => {
    // if (!shortLinkIsCustom) {
    if (shortLinkIdList.length === 0) {
      dispatch(getShortLinkCandidatesGQL({ links: 1 }))
    } else {
      setShortLink(shortLinkIdList[0])
      setShortLinkKey(shortid.generate())
      // setShortLinkIsCustom(false)
    }
    // }
  }, [shortLinkIdList])

  useEffect(() => {
    if (onChangeStatus) {
      onChangeStatus(shortLinkStatus)
    }
  }, [shortLinkStatus])

  useEffect(() => {
    if (shortLinkIsCustom) {
      const found = getItemByKeyValue(
        validateShortLinkCandidate,
        'shortLinkID',
        shortLink,
      )
      if (found !== -1) {
        const { isAvailable } = found
        const isMaxLength = shortLink.length > maxShortLinkAliasLength
        if (isMaxLength) {
          setShortLinkMessage(
            'Short link cannot be more than **1024 characters**.',
          )
          setShortLinkStatus('invalid')
        } else if (isAvailable) {
          setShortLinkMessage('Short link alias **available**.')
          setShortLinkStatus('valid')
        } else {
          setShortLinkMessage(
            'Short link **is already in use**. Contact us to upgrade to a branded domain so you can use this link.',
          )
          setShortLinkStatus('invalid')
        }
      }
    } else {
      setShortLinkMessage('')
      setShortLinkStatus('')
    }
  }, [validateShortLinkCandidate.length, shortLink])

  return (
    <>
      <ShortLinkInput
        showDropDownOnTop={showDropDownOnTop}
        editable={canChangeShortLink}
        key={shortLinkKey}
        shortLink={shortLink}
        onBeforeChange={(v) => {
          const rep = new RegExp(/[\\\/#%+\?\s]/, 'gi')
          return v.replace(rep, '-').slice(0, maxShortLinkAliasLength)
        }}
        onClickShowBranded={() => {
          if (setShowBrandedLinkModal) {
            setShowBrandedLinkModal(true)
          }
        }}
        onBlur={() => {
          if (shortLink === '') {
            setShortLinkIsCustom(false)
          }
        }}
        error={shortLinkStatus !== '' && shortLinkStatus !== 'valid'}
        onChange={(val) => {
          setShortLinkIsCustom(true)
          setShortLink(val)
          const found = getItemByKeyValue(
            validateShortLinkCandidate,
            'shortLinkID',
            val,
          )
          if (found === -1 && val !== '') {
            dispatch(
              validateShortLinkCandidateGQL({
                testCandidate: val,
              }),
            )
          }
        }}
        onRefresh={() => {
          setShortLinkKey(shortid.generate())
          setShortLink('')
          setShortLinkIsCustom(false)
        }}
      />
      {shortLinkMessage !== '' && (
        <div
          key={shortid.generate()}
          className={classNames(
            styles.validationCheckShortLink,
            styles.validationCheck,
            {
              [styles.urlIsBad]: shortLinkStatus === 'invalid',
              [styles.urlIsGood]: shortLinkStatus === 'valid',
            },
          )}
        >
          <div
            className={classNames(styles.validationCheckItem, {
              [styles.urlIsBad]: shortLinkStatus === 'invalid',
              [styles.urlIsGood]: shortLinkStatus === 'valid',
            })}
          >
            {shortLinkStatus === 'invalid' ? (
              <Button
                type="none"
                className={styles.messageButton}
                onClick={() => {
                  if (setShowBrandedLinkModal) {
                    setShowBrandedLinkModal(true)
                  }
                }}
              >
                <ReactMarkdown source={shortLinkMessage} linkTarget="_blank" />
              </Button>
            ) : (
              <ReactMarkdown source={shortLinkMessage} linkTarget="_blank" />
            )}
          </div>
        </div>
      )}
    </>
  )
}
